import {
  Bars3Icon,
  CalendarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronUpDownIcon,
  DocumentIcon,
  IdentificationIcon,
  ItalicIcon,
  PhotoIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { EyeIcon, MinusIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";

import {
  BucketInformation,
  Button,
  Paragraph,
  ParsingInformation,
  Small,
  WYSIWYG,
} from "@fronterahealth/frontera-ui-components";
import { HeroIconType, PopoverMenu } from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import {
  ApiReportSectionFieldsFieldTypeChoices,
  ApiReportTemplateSectionFieldFieldTypeChoices,
} from "@api/graphql/types-and-hooks";
import { FieldBlockCard } from "@components/FieldComponent/FieldBlockCard";
import { getFieldType, parseJson } from "@components/FieldComponent/utils";
import { OptionsEditor } from "@components/OptionsEditor/OptionsEditor";
import { TEMPLATE_EDITOR_TABS, TemplateEditorTab } from "@pages/Templates/TemplateEditor";
import { ReportTemplateSectionField } from "@providers/TemplateEditorProvider";

type FieldTypeSelectionParameters = {
  label: string;
  type?: ApiReportSectionFieldsFieldTypeChoices;
  baseContent?: string;
  useOptions?: boolean;
  formatType?: string;
  icon?: HeroIconType;
};

interface FieldEditorProps {
  field: ReportTemplateSectionField;
  readonly: boolean;
  onChange: () => void;
  onFieldUpdate: (updatedField: Partial<ReportTemplateSectionField> & { id: string }) => void;
  setActiveTab?: (tab: TemplateEditorTab) => void;
}

interface InputData {
  options: string[];
  answer: string;
}

const FieldTypeMapping: FieldTypeSelectionParameters[] = [
  { label: "Text", formatType: "Separator" },
  {
    label: "Simple Text",
    type: ApiReportSectionFieldsFieldTypeChoices.SimpleText,
    baseContent: "",
    useOptions: false,
    icon: DocumentIcon,
  },
  {
    label: "Formatted Text",
    type: ApiReportSectionFieldsFieldTypeChoices.Wysiwyg,
    baseContent: "",
    useOptions: false,
    icon: ItalicIcon,
  },
  { label: "Choices", formatType: "Separator" },
  {
    label: "Dropdown",
    type: ApiReportSectionFieldsFieldTypeChoices.Select,
    baseContent: '{"options": ["Option 1", "Option 2"], "answer": null}',
    useOptions: true,
    icon: ChevronUpDownIcon,
  },
  {
    label: "CheckBoxes",
    type: ApiReportSectionFieldsFieldTypeChoices.Checkbox,
    baseContent: '{"options": ["Option 1", "Option 2"], "answer": []}',
    useOptions: true,
    icon: CheckIcon,
  },
  {
    label: "Radio Button",
    type: ApiReportSectionFieldsFieldTypeChoices.Radio,
    baseContent: '{"options": ["Option 1", "Option 2"], "answer": null}',
    useOptions: true,
    icon: CheckCircleIcon,
  },
  { label: "Time", formatType: "Separator" },
  {
    label: "Date Picker",
    type: ApiReportSectionFieldsFieldTypeChoices.Date,
    baseContent: "",
    useOptions: false,
    icon: CalendarIcon,
  },
  {
    label: "Date Time Picker",
    type: ApiReportSectionFieldsFieldTypeChoices.Datetime,
    baseContent: "",
    useOptions: false,
    icon: CalendarIcon,
  },
  { label: "Media", formatType: "Separator" },
  {
    label: "Image",
    type: ApiReportSectionFieldsFieldTypeChoices.Image,
    baseContent: "",
    useOptions: false,
    icon: PhotoIcon,
  },
  {
    label: "Assessment Image",
    type: ApiReportSectionFieldsFieldTypeChoices.AssessmentImage,
    baseContent: "",
    useOptions: false,
    icon: PhotoIcon,
  },
  { label: "Signature", formatType: "Separator" },
  {
    label: "Signature Line",
    type: ApiReportSectionFieldsFieldTypeChoices.BlankSignatureLine,
    baseContent: "",
    useOptions: false,
    icon: MinusIcon,
  },
  {
    label: "Digital Signature",
    type: ApiReportSectionFieldsFieldTypeChoices.TypedSignature,
    baseContent: "",
    useOptions: false,
    icon: IdentificationIcon,
  },
  { label: "Treatment Plan", formatType: "Separator" },
  {
    label: "Goals",
    type: ApiReportSectionFieldsFieldTypeChoices.TreatmentPlan,
    baseContent: "",
    useOptions: false,
    icon: Bars3Icon,
  },
  { label: "Misc", formatType: "Separator" },
  {
    label: "Table",
    type: ApiReportSectionFieldsFieldTypeChoices.Table,
    baseContent: "",
    useOptions: false,
    icon: TableCellsIcon,
  },

  {
    label: "Assessment Appointments",
    type: ApiReportSectionFieldsFieldTypeChoices.AssessmentAppointments,
    baseContent: "",
    useOptions: false,
    icon: TableCellsIcon,
  },
  {
    label: "DSM Table",
    type: ApiReportSectionFieldsFieldTypeChoices.DsmTable,
    baseContent: "",
    useOptions: false,
    icon: TableCellsIcon,
  },
  {
    label: "Coordination of Care",
    type: ApiReportSectionFieldsFieldTypeChoices.CoordinationOfCare,
    baseContent: "",
    useOptions: false,
    icon: TableCellsIcon,
  },
  {
    label: "Treatment Options",
    type: ApiReportSectionFieldsFieldTypeChoices.TreatmentOptionTable,
    baseContent: "",
    useOptions: false,
    icon: TableCellsIcon,
  },
];

// Shared shape for the field-level boolean toggles (Required, Auditable) so
// each one doesn't need its own copy of the checkbox/label markup.
const FieldFlagCheckbox: React.FC<{
  id: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
  readonly: boolean;
}> = ({ id, label, checked, onToggle, readonly }) => (
  <div className="mt-2 flex items-center gap-2">
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onToggle}
      disabled={readonly}
      className="h-4 w-4 rounded-sm border-gray-300 text-green-600 cursor-pointer"
    />
    <label htmlFor={id} className="text-sm text-text-primary cursor-pointer select-none">
      {label}
    </label>
  </div>
);

export const FieldEditor: React.FC<FieldEditorProps> = ({ field, readonly, onChange, onFieldUpdate, setActiveTab }) => {
  const isTreatmentPlan = field.fieldType === ApiReportTemplateSectionFieldFieldTypeChoices.TreatmentPlan;

  const selectedInputTypeParams =
    FieldTypeMapping.find(
      (el) => (el.type as unknown as ApiReportTemplateSectionFieldFieldTypeChoices) === field.fieldType,
    ) ?? FieldTypeMapping[0];

  const displayOptions = selectedInputTypeParams.useOptions;
  const initialOptions = displayOptions ? parseJson<InputData>(field.content)?.options ?? [] : [];

  const [selectedType, setSelectedType] = useState<string>(selectedInputTypeParams.label);
  const [options, setOptions] = useState<string[]>(initialOptions);
  const [useOptions, setUseOptions] = useState<boolean>(displayOptions!);
  const [fieldSample, setFieldSample] = useState<ReportTemplateSectionField>(field);
  const [showDefaultContent, setShowDefaultContent] = useState(false);

  const hasDefaultContent = field.content && field.content.trim() !== "";

  const setSelected = (params: FieldTypeSelectionParameters) => {
    setSelectedType(params.label);

    if (!params) return;

    setFieldSample({
      ...fieldSample,
      fieldType: params.type! as unknown as ApiReportTemplateSectionFieldFieldTypeChoices,
      content: params.baseContent,
    });

    if (params.useOptions) {
      setOptions(
        parseJson<InputData>(params.baseContent || '{"options": ["Option 1", "Option 2"], "answer": null}')?.options ??
          [],
      );
    }
    setUseOptions(params.useOptions!);

    // Only pass the changed properties
    onFieldUpdate({
      id: field.id,
      fieldType: params.type! as unknown as ApiReportTemplateSectionFieldFieldTypeChoices,
      content: params.baseContent!,
    });
    onChange();
  };

  const handleOptionsChange = (newOptions: string[]) => {
    setOptions(newOptions);
    const newContent = `{"options": ${JSON.stringify(newOptions)}, "answer": ${field.fieldType === ApiReportTemplateSectionFieldFieldTypeChoices.Radio || field.fieldType === ApiReportTemplateSectionFieldFieldTypeChoices.Select ? "null" : "[]"}}`;

    setFieldSample({
      ...fieldSample,
      content: newContent,
    });

    // Only pass the changed property
    onFieldUpdate({
      id: field.id,
      content: newContent,
    });
    onChange();
  };

  const handleContentSave = async (value: string) => {
    fieldSample.content = value;

    // Only pass the changed property
    onFieldUpdate({
      id: field.id,
      content: value,
    });
    onChange();
  };

  useEffect(() => {
    setFieldSample(field);
  }, [field]);

  if (isTreatmentPlan) {
    return (
      <div className="bg-bg-default border-border-strong border rounded-lg p-4 mb-2">
        <div className="flex flex-col items-center gap-3 py-4">
          <Small colorType="secondary">Treatment plan goals and domains are configured in the Programming tab.</Small>
          {setActiveTab && (
            <Button
              onClick={() => setActiveTab(TEMPLATE_EDITOR_TABS.PROGRAMMING)}
              variant="secondary"
              buttonStyle="border"
              text="Go to Programming"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-default border-border-strong border rounded-lg p-4 mb-2">
      {/* Top Row: Input Type */}
      <div className="w-full mb-2">
        <Small colorType="primary" className="mb-1">
          Input Type
        </Small>
        <PopoverMenu
          openDirection="to-bottom-left"
          useSelectButton={true}
          label={selectedType}
          disabled={field.isAiGenerating || readonly}
          className="pb-1 pt-0.5"
          menuItems={FieldTypeMapping.map((params) => ({
            id: params.label,
            label: params.label,
            separator: params.formatType === "Separator",
            Icon: params.icon,
            textColorClassName: "text-text-primary",
            className: `${params.formatType === "Separator" && "[&_div]:border-none [&_p]:text-text-secondary! font-semibold px-2"} py-1! hover:bg-transparent!`,
            callback: () => setSelected(params),
          }))}
        />
      </div>

      {useOptions && (
        <div className="mt-2">
          <OptionsEditor options={options} onChange={handleOptionsChange} readonly={readonly} />
        </div>
      )}

      <FieldFlagCheckbox
        id={`required-${field.id}`}
        label="Required"
        checked={field.isRequired ?? false}
        readonly={readonly}
        onToggle={() => {
          onFieldUpdate({ id: field.id, isRequired: !field.isRequired });
          onChange();
        }}
      />

      <FieldFlagCheckbox
        id={`auditable-${field.id}`}
        label="Auditable"
        checked={field.isAuditable ?? false}
        readonly={readonly}
        onToggle={() => {
          onFieldUpdate({ id: field.id, isAuditable: !field.isAuditable });
          onChange();
        }}
      />

      {!field.isAi && (
        <div className="mt-2">
          {!hasDefaultContent && !showDefaultContent ? (
            <div className="-ml-2">
              <Button
                onClick={() => setShowDefaultContent(true)}
                appearance="text"
                text="+ Add Default Content"
                disabled={readonly}
              />
            </div>
          ) : (
            <>
              <Small colorType="primary" className="mb-1">
                Default Content
              </Small>
              <FieldBlockCard
                isTemplateEditor={true}
                permissions={{ canEdit: !readonly, canDelete: false }}
                field={fieldSample}
                isSaving={false}
                onSave={handleContentSave}
                reportFileUploadCompleteCallback={() => Promise.resolve()}
                removeFileCallback={() => Promise.resolve()}
                S3BucketUrlRetriever={function (): Promise<BucketInformation> {
                  throw new Error("shouldn't come here.");
                }}
                S3BucketUploadCallback={function (): Promise<void> {
                  throw new Error("shouldn't come here.");
                }}
                fileParseStatusRetriever={function (): Promise<ParsingInformation> {
                  throw new Error("shouldn't come here.");
                }}
              />
            </>
          )}
        </div>
      )}

      <>
        {field.placeholder && field.isAi && field.fieldType !== ApiReportTemplateSectionFieldFieldTypeChoices.Image ? (
          <div className="w-full border-limestone-100 border rounded-md mt-2">
            <div className="bg-limestone-100 w-full flex px-2 py-1 space-x-2">
              <EyeIcon className="h-4 w-4 text-text-secondary" />
              <Small displayType="loud">Preview</Small>
            </div>
            <div className="bg-white w-full rounded-b-md p-2">
              <div className="mb-1">
                <Paragraph displayType="loud" colorType="primary">
                  {field.title}
                </Paragraph>
              </div>
              <div className="max-w-full overflow-hidden wrap-break-word">
                <WYSIWYG
                  key={`${field.id}-${fieldSample.fieldType}`}
                  displayOnlyMode={true}
                  existingContent={field.placeholder}
                  onSave={async () => {}}
                  isSaving={false}
                  title={""}
                  placeholder={""}
                />
              </div>
            </div>
          </div>
        ) : (
          getFieldType(field) !== ApiReportTemplateSectionFieldFieldTypeChoices.Image && (
            <div className="w-full border-limestone-100 border rounded-md mt-2">
              <div className="bg-limestone-100 w-full flex px-2 py-1 space-x-2">
                <EyeIcon className="h-4 w-4 text-text-secondary" />
                <Small displayType="loud">Preview</Small>
              </div>
              <div className="bg-white w-full rounded-b-md p-2">
                <div className="mb-1">
                  <Paragraph displayType="loud" colorType="primary">
                    {field.title}
                  </Paragraph>
                </div>
                <div className="w-full overflow-hidden">
                  <FieldBlockCard
                    key={`${field.id}-${fieldSample.fieldType}-${field.content}`}
                    isTemplateEditor={true}
                    permissions={{ canEdit: false, canDelete: false }}
                    field={fieldSample}
                    isSaving={false}
                    onSave={async () => {}}
                    reportFileUploadCompleteCallback={() => Promise.resolve()}
                    removeFileCallback={() => Promise.resolve()}
                    S3BucketUrlRetriever={async () => {
                      throw new Error("shouldn't be called in preview");
                    }}
                    S3BucketUploadCallback={async () => {
                      throw new Error("shouldn't be called in preview");
                    }}
                    fileParseStatusRetriever={async () => {
                      throw new Error("shouldn't be called in preview");
                    }}
                  />
                </div>
              </div>
            </div>
          )
        )}
      </>
    </div>
  );
};
